import { ref, computed, watch } from '@vue/composition-api'
import { apiToastErrorV2 } from '@/@core/utils/toast'
import { formatNumber } from '@/@core/utils/utils'
import { kpEndpoint } from '@/axios'
import { getDateFromTimestamp, getLinkWithhttp } from '@/common-utils'
import constants, { CONNECTION_TABS, FILE_FORMATS } from '@/constants'
import { DOWNLOAD_FILE } from '@/store/modules/common.module'
import { GET_MARKETPLACE_DIRECT_USERS } from '@/store/modules/connections.module'
import {
  GET_DIRECT_CONNECTIONS_TABLE_COLUMNS,
  GET_MARKETPLACE_CONNECTIONS_TABLE_COLUMNS,
  salesRepField,
} from '@/table-columns-constants'
import { constants as c, utils } from '@kingpin-global/kingpin-utils-frontend'
import { getSelfOption } from '@/service/user.service'
import { LOAD_PRICE_BOOKS } from '@/store/modules/collection.module'
import store from '@/store'

const { convertValueToPercent } = utils
const { ROLES } = c
const { CONNECTION_REQUEST_STATUS } = constants
const filterIncludedFields = ['firstName', 'entityName']

export function useConnections() {
  // Reactive state
  const connectionData = ref({})
  const tableItems = ref([])
  const rawItems = ref([])
  const tableFields = ref([])
  const loading = ref(true)
  const selectedTab = ref(CONNECTION_TABS.DIRECT)
  const search = ref('')
  const currentPage = ref(1)
  const perPage = ref(10)
  const tableLength = ref(0)
  const tableDataCount = ref(0)
  const selectedRepsNames = ref([])
  const selectedSalesManagerNames = ref([])
  const userData = ref(null)
  const modalReloadKey = ref(0)

  // Connection request modal state
  const brandId = ref(null)
  const retailerId = ref(null)
  const requestId = ref(null)
  const status = ref(null)
  const requestMessage = ref(null)

  // Teammates modal state
  const selectedRetailerIdForTeammates = ref(null)
  const selectedRetailerName = ref('')

  // Import/export state
  const isExporting = ref(false)
  const directRetailersImportFile = ref(null)
  const directRetailersImportFileLink = ref(null)
  const isUploadingDirectRetailers = ref(false)
  const importFileUrl = ref(null)

  // Store-derived computeds
  const profile = computed(() => store.state.auth.profileData)
  const salesReps = computed(() => store.state.auth.salesReps)
  const salesManagers = computed(() => store.state.auth.salesManagers)
  const isBrand = computed(() => store.getters.isBrand)
  const isRetailer = computed(() => store.getters.isRetailer)
  const isEntityAdmin = computed(() => store.getters.isEntityAdmin)
  const isSalesManager = computed(() => store.getters.isSalesManager)
  const role = computed(() => store.getters.role)

  // --- getTableFields helpers ---
  function filterColumns(columns, excludedKeys) {
    return columns.filter(col => !excludedKeys.includes(col.key))
  }

  function getDirectRetailerFields() {
    const excludedKeys = ['discount', 'actions', 'nickName']
    return filterColumns(GET_DIRECT_CONNECTIONS_TABLE_COLUMNS(), excludedKeys)
  }

  function prependSalesRepColumn(columns) {
    const canSeeSalesRep = (isEntityAdmin.value || isSalesManager.value) && isBrand.value
    if (canSeeSalesRep) {
      columns.unshift(salesRepField)
    }
  }

  function insertTeammatesBeforeActions(columns) {
    const actionsIndex = columns.findIndex(col => col.key === 'actions')
    if (actionsIndex === -1) {
      return
    }
    columns.splice(actionsIndex, 0, { key: 'teammates', label: 'Teammates' })
  }

  function getDirectBrandFields() {
    const excludedKeys = ['logisticsTerms']
    const columns = GET_DIRECT_CONNECTIONS_TABLE_COLUMNS()
    prependSalesRepColumn(columns)
    insertTeammatesBeforeActions(columns)
    return filterColumns(columns, excludedKeys)
  }

  function getMarketplaceFields() {
    const columns = GET_MARKETPLACE_CONNECTIONS_TABLE_COLUMNS()
    return isRetailer.value ? filterColumns(columns, ['nickName']) : columns
  }

  // Page computeds
  const getTableFields = computed(() => {
    if (selectedTab.value !== CONNECTION_TABS.DIRECT) {
      return getMarketplaceFields()
    }
    return isRetailer.value ? getDirectRetailerFields() : getDirectBrandFields()
  })

  const getRoleText = computed(() => {
    if (isBrand.value) {
      return ROLES.RETAILER
    }
    return ROLES.BRAND
  })

  const getTextForPlaceholder = computed(() => `Search by Name or ${isRetailer.value ? 'Brand' : 'Store'} Name`)

  const isShowImportExport = computed(() => isBrand.value && selectedTab.value === CONNECTION_TABS.DIRECT)

  const hasDirectUsers = computed(() => selectedTab.value === CONNECTION_TABS.DIRECT && tableItems.value?.length > 0)

  // --- onLoadTab helpers ---
  const TAB_DATA_KEYS = {
    [CONNECTION_TABS.DIRECT]: { brand: 'directRetailers', retailer: 'directBrands' },
    [CONNECTION_TABS.MARKETPLACE]: { brand: 'marketplaceRetailers', retailer: 'marketplaceBrands' },
  }

  function getItemsForCurrentTab() {
    const mapping = TAB_DATA_KEYS[selectedTab.value]
    const key = isBrand.value ? mapping.brand : mapping.retailer
    return connectionData.value[key]
  }

  const SOCIAL_LINK_FIELDS = ['websiteLink', 'instagramLink', 'facebookLink', 'twitterLink', 'linkedinLink']

  function buildSocialLinks(item) {
    return SOCIAL_LINK_FIELDS.reduce((links, field) => {
      links[field] = getLinkWithhttp(item?.[field]) || ''
      return links
    }, {})
  }

  function enrichItemsWithSocialLinks(items) {
    return items?.map(item => ({
      ...item,
      socialLinks: buildSocialLinks(item),
    })) || []
  }

  // Methods
  function onLoadTab(tab = '') {
    if (tab) {
      selectedTab.value = tab
    }
    search.value = ''
    tableFields.value = getTableFields.value
    const items = getItemsForCurrentTab()
    tableItems.value = enrichItemsWithSocialLinks(items)
    tableLength.value = items?.length || 0
    tableDataCount.value = tableItems.value.length
    rawItems.value = tableItems.value
  }

  async function getData(isLive = false) {
    if (!isLive) {
      loading.value = true
      tableItems.value = []
    }
    try {
      const res = await store.dispatch(GET_MARKETPLACE_DIRECT_USERS)
      const data = res?.data?.data
      connectionData.value = data
      onLoadTab()
      loading.value = false
    } catch (err) {
      loading.value = false
      console.error(err)
    }
  }

  function matchesSelectedReps(item, selectedNames, isSelectedSelf) {
    return selectedNames.includes(item[salesRepField.key])
      || (isSelectedSelf && item.isCreatedBySelf)
  }

  function updateFilter() {
    const selectedNames = [...selectedRepsNames.value, ...selectedSalesManagerNames.value]
    if (selectedNames.length) {
      const isSelectedSelf = selectedNames.includes(getSelfOption().name)
      tableItems.value = rawItems.value.filter(
        item => matchesSelectedReps(item, selectedNames, isSelectedSelf),
      )
    } else {
      tableItems.value = rawItems.value
    }
    tableDataCount.value = tableItems.value.length
  }

  function onFilterBySalesRep(selectedValues = []) {
    selectedRepsNames.value = selectedValues.map(s => s.name)
    updateFilter()
  }

  function onFilterBySalesManager(selectedValues = []) {
    selectedSalesManagerNames.value = selectedValues.map(s => s.name)
    updateFilter()
  }

  function editUser(data) {
    userData.value = data
    modalReloadKey.value += 1
  }

  function getDicountWithPercent(discount = 1) {
    if (discount > 1) {
      return 100 + convertValueToPercent(discount)
    }
    return convertValueToPercent(discount)
  }

  function setConnectionRequestTarget(item) {
    brandId.value = item.brandId
    retailerId.value = item.retailerId
    requestId.value = item.requestId
    status.value = item.status
    requestMessage.value = item.requestMessage
  }

  function setTeammatesTarget(item) {
    selectedRetailerIdForTeammates.value = item.retailerId
    selectedRetailerName.value = item.entityName || ''
  }

  function exportDirectUsers() {
    isExporting.value = true
    const userName = profile.value?.firstName || ''
    const payload = {
      url: kpEndpoint.user.connection.export,
      fileName: `direct-retailers${userName ? `-of-${userName}` : ''}.xlsx`,
      fileFormat: FILE_FORMATS.XLSX,
      successMessage: 'File exported successfully',
    }
    store.dispatch(DOWNLOAD_FILE, payload)
      .catch(err => {
        apiToastErrorV2(err)
      })
      .finally(() => {
        isExporting.value = false
      })
  }

  function resetImportState() {
    directRetailersImportFile.value = null
    directRetailersImportFileLink.value = null
    isUploadingDirectRetailers.value = false
    importFileUrl.value = null
  }

  function initConnections() {
    getData()
    store.dispatch(LOAD_PRICE_BOOKS)
  }

  // Watch role changes
  watch(role, () => {
    onLoadTab()
  })

  return {
    // State
    connectionData,
    tableItems,
    rawItems,
    tableFields,
    loading,
    selectedTab,
    search,
    currentPage,
    perPage,
    tableLength,
    tableDataCount,
    selectedRepsNames,
    selectedSalesManagerNames,
    userData,
    modalReloadKey,
    brandId,
    retailerId,
    requestId,
    status,
    requestMessage,
    selectedRetailerIdForTeammates,
    selectedRetailerName,
    isExporting,
    directRetailersImportFile,
    directRetailersImportFileLink,
    isUploadingDirectRetailers,
    importFileUrl,

    // Computeds
    profile,
    salesReps,
    salesManagers,
    isBrand,
    isRetailer,
    isEntityAdmin,
    isSalesManager,
    getTableFields,
    getRoleText,
    getTextForPlaceholder,
    isShowImportExport,
    hasDirectUsers,

    // Methods
    onLoadTab,
    getData,
    updateFilter,
    onFilterBySalesRep,
    onFilterBySalesManager,
    editUser,
    getDicountWithPercent,
    setConnectionRequestTarget,
    setTeammatesTarget,
    exportDirectUsers,
    resetImportState,
    initConnections,

    // Constants & utilities
    CONNECTION_TABS,
    CONNECTION_REQUEST_STATUS,
    getDateFromTimestamp,
    getLinkWithhttp,
    formatNumber,
    convertValueToPercent,
    filterIncludedFields,
  }
}
